import React, { useState, useEffect, useContext } from 'react'
import { LeagueContext, SeasonContext } from '../../Context'
import { PageHeader, SectionHeading } from '../../Components/Headers'
import {
  PageContainer,
  Box,
  BoxWithColumns,
  SidePanelLayout,
  MainColumn,
  SidePanel,
} from '../../Components/Layout'
import { AutocompleteContainer } from '../../Components/Search'
import { getBanner, SEASON_SEGMENT_NAMES } from '../../Utils'
import { FavoriteTeamCon } from '../../Components/FavoriteTeam/FavoriteTeamCon'
import { Banner, Feed } from '../../Api'
import {
  SecondaryMobileBanner,
  SponsorTopBanner,
  SideBanner,
} from '../../Components/Layout/sponsorBanners'
import { HomeOfferings } from '../../Components/HomeOfferings'
import { SponsorSection } from '../../Components/Sponsors'
import { BottomAppCallout } from '../../Components/Layout/Overlays'
import { APP_LINKS } from '../../Utils/DynamicLinks/dynamicLinkHelpers'
import { FeedHighlightSection } from './components/Feed/FeedHighlightSection'
import { HeadlinesFromLeague } from './components/Feed/HeadlinesFromLeague'
import { ShopList } from '../../Components/Shop/ShopList/ShopList'
import { Root } from '../../Components/Layout/PageContainer'
import { AdUnit } from '../../Components/Ads/AdUnit'
import { STATS_YEAR } from '../../Config/League/league.config'
import { ContentFeedList } from './components/ContentFeeds/ContentFeedList'
import { StandingsSection } from '../../Components/Standings/StandingsSection'
import { BracketContainer } from '../../Components/Bracket/bracketContainer'
import { useDynamicTheme } from '../../Hooks/Theme/useDynamicTheme'
import { Con } from '../../Components/Layout/Containers/ConStyles'
import { SEASON_SELECTOR_TYPES } from '../../Constants/season'
import { SlideDownFade } from '../../Components/Layout/Animations/SlideDownFade'
import { CONTENT_CARD_LOCATIONS } from '../../Constants/braze'
import { HomeContentCard } from './components/ContentCards/HomeContentCard'
import { GameTicker } from '../../Components/GameTicker/GameTicker'
import { Head } from '../../Components/Typography/Text.styles'

const Homepage: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { seasonSelect } = useContext(SeasonContext)!
  const [banner, setBanner] = useState<Banner | null>(null)
  const [mainFeed, setMainFeed] = useState<Feed | null>(null)
  const { feed, banners, standings, bestSellingProducts } = useContext(LeagueContext)!
  const { isMobile } = useDynamicTheme()

  useEffect(() => {
    if (banners.length > 0) {
      const b = getBanner(banners, 'homepage')
      if (!b) return
      setBanner(b)
    }
    if (feed) {
      setMainFeed(feed)
    }
  }, [banners, feed])

  return (
    <Root>
      {banner?.sponsor && banner?.leftSideUrl && (
        <SideBanner
          banner={banner}
          img={banner.leftSideUrl}
          side="left"
          link={banner.topLink}
        />
      )}

      <PageContainer sponsor={banner?.sponsor ? true : false}>
        {banner && !banner.sponsor && (
          <PageHeader banner={banner} img={banner.topUrl} link={banner.topLink} />
        )}
        {banner && banner.sponsor && (
          <SponsorTopBanner img={banner.topUrl} link={banner.topLink} banner={banner} />
        )}
        <AutocompleteContainer />

        {!banner?.sponsor && (
          <AdUnit bannerType="bannerMain" aria-label="Homepage ad first" />
        )}
        <BracketContainer />
        <GameTicker />
        <SidePanelLayout panelOn="right">
          <MainColumn>
            <Box padding={6}>
              <SlideDownFade>
                <Head sideMargins={2}>
                  League Home
                </Head>
                <FeedHighlightSection />
              </SlideDownFade>
              <SlideDownFade>
                <HeadlinesFromLeague />
              </SlideDownFade>
            </Box>
          </MainColumn>

          <SidePanel>
            <BoxWithColumns layout="aa" padding={2} centerVertically={true}>
              <div>
                <HomeContentCard location={CONTENT_CARD_LOCATIONS.HOME_SIDE_CARD_1} />
              </div>
              <div>
                {standings.length > 0 && (
                  <Con direction="column">
                    <SectionHeading>
                      {seasonSelect[SEASON_SELECTOR_TYPES.STANDINGS].seasonType ===
                        SEASON_SEGMENT_NAMES.champseries && (
                        <>
                          <strong>Championship Series standings</strong>
                        </>
                      )}
                      {seasonSelect[SEASON_SELECTOR_TYPES.STANDINGS].seasonType !==
                        SEASON_SEGMENT_NAMES.champseries && (
                        <>
                          <strong>{STATS_YEAR} Season standings</strong>
                        </>
                      )}
                    </SectionHeading>
                    <StandingsSection
                      items={standings}
                      seasonSelect={seasonSelect[SEASON_SELECTOR_TYPES.STANDINGS]}
                      section={'home_page'}
                      full={true}
                    />
                  </Con>
                )}
              </div>
              <div>
                <HomeContentCard
                  location={CONTENT_CARD_LOCATIONS.HOME_SIDE_CARD_2}
                />
              </div>
            </BoxWithColumns>
          </SidePanel>
        </SidePanelLayout>

        {banner?.secondaryMobile && (
          <SecondaryMobileBanner
            img={banner.secondaryMobile}
            link={banner.topLink}
            banner={banner}
          />
        )}
        {!banner?.secondaryMobile && !isMobile && (
          <AdUnit bannerType="bannerSecond" aria-label="Homepage ad second" />
        )}

        {bestSellingProducts.length > 0 && (
          <SlideDownFade>
            <Box>
              <ShopList
                products={bestSellingProducts}
                medium={'Web_Main_Collection'}
                campaign={'web_trending_products'}
                listTitle="Trending in the Shop"
              />
            </Box>
          </SlideDownFade>
        )}

        {mainFeed?.latest && mainFeed.latest.length > 0 && (
          <ContentFeedList mainFeed={mainFeed.latest} />
        )}

        {banner?.secondaryMobile && (
          <SecondaryMobileBanner img={banner.secondaryMobile} link={banner.topLink} />
        )}
        {!isMobile && banner?.secondaryMobile && (
          <AdUnit bannerType="bannerThird" aria-label="Homepage ad third" />
        )}

        <FavoriteTeamCon />
        <HomeOfferings />

        <SponsorSection />
      </PageContainer>

      {banner?.sponsor && banner?.rightSideUrl && (
        <SideBanner
          img={banner.rightSideUrl}
          side="right"
          link={banner.topLink}
          banner={banner}
        />
      )}
      <BottomAppCallout appLink={APP_LINKS.homeCallout} location="homepage" />
    </Root>
  )
}

export default Homepage
